<script>
import { useEsignStore } from '../../stores/esign'
import { extractDeepErrorMessage } from '../../composables/useApiErrors'
import EcSpinner from '../ui/EcSpinner.vue'
import EcAlert from '../ui/EcAlert.vue'
import EcDrawer from '../ui/EcDrawer.vue'
import EcButton from '../ui/EcButton.vue'
import EcPagination from '../ui/EcPagination.vue'
import EcActionMenu from '../ui/EcActionMenu.vue'
import TemplateForm from './TemplateForm.vue'
import TemplateDetail from './TemplateDetail.vue'

export default {
  name: 'TemplateList',
  components: {
    EcSpinner,
    EcAlert,
    EcDrawer,
    EcButton,
    EcPagination,
    EcActionMenu,
    TemplateForm,
    TemplateDetail,
  },
  props: {
    holderType: {
      type: String,
      required: true,
    },
    holderId: {
      type: [String, Number],
      required: true,
    },
    clickableRows: {
      type: Boolean,
      default: false,
    },
    actionsMode: {
      type: String,
      default: 'inline',
    },
  },
  data() {
    var store = useEsignStore()
    return {
      store: store,
      loading: true,
      templates: [],
      total: 0,
      query: '',
      sort: '-created_at',
      perPage: 10,
      currentPage: 1,
      perPageOptions: [10, 20, 50, 100],
      showFormDrawer: false,
      showDetailDrawer: false,
      detailDrawerFullscreen: false,
      editingTemplate: null,
      viewingTemplate: null,
      debounceTimer: null,
    }
  },
  computed: {
    t() {
      return this.$ecTranslate
    },
    from() {
      return this.perPage * (this.currentPage - 1)
    },
    to() {
      var high = this.from + this.perPage
      return this.total < high ? this.total : high
    },
    totalPages() {
      return Math.ceil(this.total / this.perPage) || 1
    },
  },
  watch: {
    query() {
      this.fetchDebounced()
    },
    perPage() {
      this.currentPage = 1
      this.fetchTemplates()
    },
    currentPage() {
      this.fetchTemplates()
    },
  },
  mounted() {
    this.fetchTemplates()
  },
  methods: {
    getActions(tpl) {
      var t = this.$ecTranslate
      return [
        { key: 'view', label: t('ELIXIO_CONNECT.VIEW'), icon: 'eye' },
        { key: 'edit', label: t('ELIXIO_CONNECT.EDIT'), icon: 'pencil' },
        { key: 'delete', label: t('ELIXIO_CONNECT.DELETE'), icon: 'trash', variant: 'danger', separator: true },
      ]
    },
    onActionSelect(key, tpl) {
      if (key === 'view') this.openView(tpl)
      else if (key === 'edit') this.openEdit(tpl)
      else if (key === 'delete') this.handleDelete(tpl)
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString()
    },
    fetchDebounced() {
      var self = this
      if (this.debounceTimer) clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(function () {
        self.currentPage = 1
        self.fetchTemplates()
      }, 300)
    },
    async fetchTemplates() {
      this.loading = true
      try {
        var params = {}
        if (this.sort) params.sort = this.sort
        params.filter = {}
        if (this.query) params.filter.search = this.query
        params.page = {
          number: this.currentPage,
          size: this.perPage,
        }
        var result = await this.store.listTemplates(this.holderType, this.holderId, params)
        if (Array.isArray(result)) {
          this.templates = result
          this.total = result.length
        } else if (result && typeof result === 'object') {
          if (Array.isArray(result.data)) {
            this.templates = result.data
            this.total = (result.meta && result.meta.total) || this.templates.length
          } else if (result.id) {
            this.templates = [result]
            this.total = 1
          } else {
            this.templates = []
            this.total = 0
          }
        }
      } finally {
        this.loading = false
      }
    },
    onSortColumn(column) {
      if (this.sort === column) {
        this.sort = '-' + column
      } else if (this.sort === '-' + column) {
        this.sort = column
      } else {
        this.sort = '-' + column
      }
      this.fetchTemplates()
    },
    sortIcon(column) {
      if (this.sort === column) return 'sort-up'
      if (this.sort === '-' + column) return 'sort-down'
      return 'arrow-down-up'
    },
    resetFilters() {
      this.query = ''
      this.currentPage = 1
      this.sort = '-created_at'
      this.fetchTemplates()
    },
    openCreate() {
      this.editingTemplate = null
      this.showFormDrawer = true
    },
    openEdit(template) {
      this.editingTemplate = template
      this.showFormDrawer = true
    },
    openView(template) {
      this.viewingTemplate = template
      this.detailDrawerFullscreen = false
      this.showDetailDrawer = true
    },
    onRowClick(tpl) {
      if (this.clickableRows) {
        this.openView(tpl)
      }
    },
    async handleDelete(template) {
      var t = this.$ecTranslate
      var confirmed = this.$ecConfirm
        ? await this.$ecConfirm(t('ELIXIO_CONNECT.DELETE_THIS_MODEL'))
        : window.confirm(t('ELIXIO_CONNECT.DELETE_THIS_MODEL'))
      if (!confirmed) return

      try {
        await this.store.deleteTemplate(this.holderType, this.holderId, template.id)
        this.$ecNotify(t('ELIXIO_CONNECT.MODELS_DELETED'), 'success')
        await this.fetchTemplates()
      } catch (err) {
        this.$ecNotify(extractDeepErrorMessage(err, t('ELIXIO_CONNECT.SOMETHING_WENT_WRONG')), 'error')
      }
    },
    async onFormSaved(createdTemplate) {
      var wasCreating = !this.editingTemplate
      this.showFormDrawer = false
      this.editingTemplate = null
      await this.fetchTemplates()

      // After creation, open detail drawer automatically
      if (wasCreating && createdTemplate && createdTemplate.id) {
        var freshTemplate = this.templates.find(function (t) { return t.id === createdTemplate.id })
        this.viewingTemplate = freshTemplate || createdTemplate
        this.showDetailDrawer = true
      }
    },
    onDetailRequestFullscreen() {
      this.detailDrawerFullscreen = true
    },
    onDetailEdit(template) {
      this.showDetailDrawer = false
      this.openEdit(template)
    },
    onDetailDelete(template) {
      this.showDetailDrawer = false
      this.handleDelete(template)
    },
  },
}
</script>

<template>
  <div class="ec-template-list">
    <!-- Title bar -->
    <div class="ec-flex ec-justify-between ec-items-center ec-mb-3">
      <h5 style="margin: 0">{{ t('ELIXIO_CONNECT.MODELS_LIST') }}</h5>
      <EcButton variant="primary" size="sm" icon="plus-lg" @click="openCreate">
        {{ t('ELIXIO_CONNECT.ADD_MODEL') }}
      </EcButton>
    </div>

    <!-- Toolbar: search + per-page + reset -->
    <div class="ec-template-list__toolbar">
      <div class="ec-template-list__toolbar-search">
        <div class="ec-input ec-input--has-icon">
          <i class="bi bi-search ec-input__icon" />
          <input
            v-model="query"
            type="search"
            class="ec-input__field"
            :placeholder="t('ELIXIO_CONNECT.SEARCH')"
          />
        </div>
      </div>

      <div class="ec-template-list__toolbar-right">
        <select
          v-model="perPage"
          class="ec-select__field ec-template-list__per-page"
        >
          <option v-for="opt in perPageOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <EcButton
          variant="outline-secondary"
          size="sm"
          icon="arrow-counterclockwise"
          :title="'Reset'"
          @click="resetFilters"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ec-text-center" style="padding: 2rem 0">
      <EcSpinner />
    </div>

    <!-- Empty state -->
    <EcAlert v-else-if="!templates.length" type="info">
      {{ query ? t('ELIXIO_CONNECT.NO_FIELD_FOUND') : 'Aucun modèle trouvé. Créez votre premier modèle pour commencer.' }}
    </EcAlert>

    <!-- Templates table -->
    <template v-else>
      <div class="ec-template-list__table">
        <div class="ec-table-responsive">
        <table class="ec-table ec-table-hover ec-table-sm">
          <thead>
            <tr>
              <th>{{ t('ELIXIO_CONNECT.CODE') }}</th>
              <th
                class="ec-table__sortable"
                @click="onSortColumn('name')"
              >
                {{ t('ELIXIO_CONNECT.NAME') }}
                <i :class="'bi bi-' + sortIcon('name')" class="ec-table__sort-icon" />
              </th>
              <th>{{ t('ELIXIO_CONNECT.EXCERPT') }}</th>
              <th
                class="ec-table__sortable"
                @click="onSortColumn('created_at')"
              >
                {{ t('ELIXIO_CONNECT.CREATED_AT') }}
                <i :class="'bi bi-' + sortIcon('created_at')" class="ec-table__sort-icon" />
              </th>
              <th style="text-align: right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="tpl in templates"
              :key="tpl.id"
              :class="{ 'ec-table__row--clickable': clickableRows }"
              @click="onRowClick(tpl)"
            >
              <td>
                <span class="ec-text-muted" style="font-size: 0.8125rem">{{ tpl.code || '-' }}</span>
              </td>
              <td>
                <span class="ec-fw-medium">{{ tpl.name }}</span>
              </td>
              <td>
                <span class="ec-text-muted" style="font-size: 0.8125rem">
                  {{ tpl.excerpt || tpl.description || '-' }}
                </span>
              </td>
              <td>{{ formatDate(tpl.created_at) }}</td>
              <td style="text-align: right" @click.stop>
                <!-- Inline actions (default) -->
                <div v-if="actionsMode === 'inline'" class="ec-flex ec-gap-1" style="justify-content: flex-end">
                  <EcButton
                    variant="outline-secondary"
                    size="sm"
                    icon="eye"
                    :title="t('ELIXIO_CONNECT.VIEW')"
                    @click="openView(tpl)"
                  />
                  <EcButton
                    variant="outline-secondary"
                    size="sm"
                    icon="pencil"
                    :title="t('ELIXIO_CONNECT.EDIT')"
                    @click="openEdit(tpl)"
                  />
                  <EcButton
                    variant="outline-danger"
                    size="sm"
                    icon="trash"
                    :title="t('ELIXIO_CONNECT.DELETE')"
                    @click="handleDelete(tpl)"
                  />
                </div>

                <!-- Dropdown actions -->
                <div v-else style="display: flex; justify-content: flex-end">
                  <EcActionMenu
                    :actions="getActions(tpl)"
                    @select="onActionSelect($event, tpl)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- Footer: info + pagination -->
        <div class="ec-template-list__footer">
          <p class="ec-text-muted" style="font-size: 0.8125rem; margin: 0">
            {{
              t('ELIXIO_CONNECT.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES', {
                from: total ? from + 1 : 0,
                to: to,
                of: total,
              })
            }}
          </p>
          <EcPagination
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @update:current-page="currentPage = $event"
          />
        </div>
      </div>
    </template>

    <!-- Create / Edit drawer -->
    <EcDrawer
      :value="showFormDrawer"
      :title="editingTemplate ? t('ELIXIO_CONNECT.EDIT_MODEL') : t('ELIXIO_CONNECT.ADD_MODEL')"
      width="1000px"
      resizable
      @input="showFormDrawer = $event"
    >
      <TemplateForm
        :holder-type="holderType"
        :holder-id="holderId"
        :template="editingTemplate"
        @saved="onFormSaved"
        @cancel="showFormDrawer = false"
      />
    </EcDrawer>

    <!-- View drawer -->
    <EcDrawer
      :value="showDetailDrawer"
      :title="t('ELIXIO_CONNECT.VIEW_MODEL')"
      width="1000px"
      resizable
      :fullscreen="detailDrawerFullscreen"
      @input="showDetailDrawer = $event"
    >
      <TemplateDetail
        v-if="viewingTemplate"
        :holder-type="holderType"
        :holder-id="holderId"
        :template="viewingTemplate"
        @edit="onDetailEdit"
        @delete="onDetailDelete"
        @request-fullscreen="onDetailRequestFullscreen"
      />
    </EcDrawer>
  </div>
</template>

<style scoped>
.ec-template-list__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.ec-template-list__toolbar-search {
  flex: 1;
  min-width: 200px;
}

.ec-template-list__toolbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.ec-template-list__per-page {
  width: auto;
  min-width: 70px;
}

.ec-template-list__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
